import type { ContactSummary, MergeContactsResult, MergePlan } from '@performance-crm/shared';
import { AlertTriangle, GitMerge } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast-context';
import { STAGE_LABEL } from '@/lib/format';
import { useMergeContacts, useMergePreview } from '@/lib/queries';

export const MERGE_CONFIRM_WORD = 'fusionar';

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

function ContactLine({ c }: { c: ContactSummary }) {
  return (
    <div className="text-sm">
      <span className="font-medium">{c.name}</span>{' '}
      <span className="text-muted-foreground">
        {c.phone ?? '—'} · {c.email ?? '—'} · {c.documentNumber ?? '—'}
      </span>
      {c.doNotContact && (
        <Badge tone="danger" className="ml-2">
          No contactar
        </Badge>
      )}
    </div>
  );
}

/** Resumen de lo que va a pasar: qué se une, qué queda como alterno, qué lead se cierra y qué archivos se mueven. */
export function MergeSummary({ plan }: { plan: MergePlan }) {
  const alternates = [
    ...plan.alternates.phones.map((v) => `celular ${v}`),
    ...plan.alternates.emails.map((v) => `correo ${v}`),
    ...plan.alternates.documents.map((v) => `documento ${v}`),
  ];
  const filled = [
    ...(plan.filled.phone ? [`celular ${plan.filled.phone}`] : []),
    ...(plan.filled.email ? [`correo ${plan.filled.email}`] : []),
    ...(plan.filled.documentNumber ? [`documento ${plan.filled.documentNumber}`] : []),
  ];
  return (
    <div className="space-y-3 text-sm" data-testid="merge-summary">
      <div>
        <p className="text-muted-foreground text-xs font-medium uppercase">Se conserva</p>
        <ContactLine c={plan.target} />
      </div>
      <div>
        <p className="text-muted-foreground text-xs font-medium uppercase">
          {plan.sources.length === 1 ? 'Desaparece' : 'Desaparecen'}
        </p>
        {plan.sources.map((c) => (
          <ContactLine key={c.id} c={c} />
        ))}
      </div>
      {plan.differentDocuments && (
        <p
          className="text-danger flex items-start gap-2 text-sm"
          data-testid="merge-different-docs"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Los documentos no coinciden: puede que sean dos personas distintas. Revisa antes de
          fusionar.
        </p>
      )}
      <ul className="list-disc space-y-1 pl-5">
        <li>
          {alternates.length
            ? `Quedan como datos alternos del conservado (la identidad los encuentra igual): ${alternates.join(', ')}.`
            : 'No hay datos distintos que guardar como alternos.'}
        </li>
        {filled.length > 0 && (
          <li>Se completan datos vacíos del conservado: {filled.join(', ')}.</li>
        )}
        {plan.joins.map((j) => (
          <li key={j.sourceLeadId} data-testid="merge-join">
            Ambos tienen lead activo en <span className="font-medium">{j.projectName}</span>: se
            unirán. El lead de {j.sourceAdvisorName ?? 'la bolsa'} (
            {plural(j.interactions, 'gestión', 'gestiones')},{' '}
            {plural(j.messages, 'mensaje', 'mensajes')}) se cierra con motivo «fusionado» dentro del
            lead de {j.targetAdvisorName ?? 'la bolsa'}; no cuenta como caída ni desistimiento.
          </li>
        ))}
        {plan.movedLeadIds.length > 0 && (
          <li>
            {plural(plan.movedLeadIds.length, 'lead pasa', 'leads pasan')} tal cual al conservado
            (otros proyectos o ya cerrados).
          </li>
        )}
        <li>
          {plan.movedAttachments
            ? `${plural(plan.movedAttachments, 'archivo del cliente pasa', 'archivos del cliente pasan')} al conservado.`
            : 'No hay archivos del cliente que mover.'}
        </li>
        {(plan.target.doNotContact || plan.sources.some((s) => s.doNotContact)) && (
          <li>Alguno tiene «no contactar»: el conservado lo mantiene.</li>
        )}
      </ul>
    </div>
  );
}

/**
 * Doble confirmación de la fusión (decisión del dueño): vista previa de lo que va a pasar y hay que escribir
 * «fusionar» para continuar. No se puede deshacer.
 */
export function MergeContactsDialog({
  targetContactId,
  sourceContactIds,
  open,
  onOpenChange,
  onMerged,
}: {
  targetContactId: string;
  sourceContactIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onMerged?: (result: MergeContactsResult) => void;
}) {
  const toast = useToast();
  const merge = useMergeContacts();
  const preview = useMergePreview(open ? targetContactId : undefined, sourceContactIds);
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);
  const ready = typed.trim().toLowerCase() === MERGE_CONFIRM_WORD && !!preview.data;
  const run = async () => {
    try {
      const result = await merge.mutateAsync({ targetContactId, sourceContactIds });
      toast.success(
        'Personas fusionadas',
        `${result.movedLeads + result.joinedLeads} leads y ${result.movedAttachments} archivos pasaron al conservado`,
      );
      onOpenChange(false);
      onMerged?.(result);
    } catch (err) {
      toast.error('No se pudo fusionar', (err as Error).message);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5" /> Fusionar personas repetidas
          </DialogTitle>
          <DialogDescription>
            Revisa lo que va a pasar. La fusión no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        {preview.isLoading && <Skeleton className="h-32 w-full" />}
        {preview.error && (
          <p className="text-danger text-sm">
            No se pudo preparar la fusión: {(preview.error as Error).message}
          </p>
        )}
        {preview.data && <MergeSummary plan={preview.data} />}
        <div className="space-y-1">
          <Label htmlFor="merge-confirm">Para confirmar, escribe «{MERGE_CONFIRM_WORD}»</Label>
          <Input
            id="merge-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={MERGE_CONFIRM_WORD}
            autoComplete="off"
            data-testid="merge-confirm"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void run()}
            disabled={!ready || merge.isPending}
            data-testid="merge-run"
          >
            <GitMerge className="h-4 w-4" /> {merge.isPending ? 'Fusionando…' : 'Fusionar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Lead resumido para las tarjetas de duplicados: proyecto · etapa · asesor (y si está cerrado). */
export function LeadSummaryLine({ lead }: { lead: ContactSummary['leads'][number] }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span>{lead.projectName}</span>
      <span className="text-muted-foreground">
        · {STAGE_LABEL[lead.pipelineStage]} · {lead.advisorName ?? 'sin asesor'}
      </span>
      {lead.status !== 'ACTIVO' && <Badge tone="muted">Cerrado</Badge>}
    </span>
  );
}
